/* eslint-disable @typescript-eslint/ban-types */
import React from 'react';
import { useMutation } from 'react-query';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import Input from 'v2/form/Input/Input';
import { Button, Container } from 'react-bootstrap';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import BackgroundImages from 'v2/common/Background/BackgroundImages';
import { PROPOSAL_AFIRMATIVE } from 'components/proposal_types';
import restFactory from 'v2/utils/restFactory';
import { AxiosError } from 'axios';

interface AcceptProposalForm {
  userEmail: string;
  proposalId: number;
}

const schema = yup.object().shape({
  userEmail: yup.string().email().required('Required'),
  proposalId: yup.number().required('Required'),
});

const AcceptProposal = () => {
  const {
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<AcceptProposalForm>({
    mode: 'onChange',
    resolver: yupResolver(schema),
  });

  const { isLoading, mutate: submitProposal } = useMutation(
    ({ userEmail, proposalId }: AcceptProposalForm) =>
      restFactory.put(
        `/proposals/${proposalId}`,
        { action: PROPOSAL_AFIRMATIVE },
        { 'Ghost-User': userEmail }
      ),
    {
      onMutate: async ({ userEmail, proposalId }: AcceptProposalForm) => {
        await restFactory.post(
          `/conversations`,
          { proposal: proposalId },
          { 'Ghost-User': userEmail }
        );
      },
      onSuccess: () => {
        toast.success('Proposal successfully accepted');
        reset();
      },
      onError: (error: AxiosError) => {
        toast.error(error);
      },
    }
  );

  return (
    <Container className="mt-4 fix-bg-images" fluid="xl">
      <BackgroundImages showTopImage={false} />
      <h1>Accept Proposal</h1>
      <form onSubmit={handleSubmit((data) => submitProposal(data))}>
        <div className="d-flex w-100">
          <div className="form-group w-50 mr-2">
            <Input
              label="Proposal ID"
              type="number"
              register={register('proposalId', { required: true })}
              error={errors?.proposalId && 'Must be a valid number'}
              autoComplete="off"
              required={true}
              step="1"
              showErrorBorder={!!errors?.proposalId?.message}
              disabled={isLoading}
            />
          </div>
          <div className="form-group w-50 ml-2">
            <Input
              label="User Email"
              type="email"
              register={register('userEmail', { required: true })}
              error={errors?.userEmail && 'Must be a valid email'}
              autoComplete="off"
              required={true}
              step="1"
              showErrorBorder={!!errors?.userEmail?.message}
              disabled={isLoading}
            />
          </div>
        </div>
        <Button variant="primary" type="submit" disabled={isLoading}>
          {isLoading ? 'Loading...' : 'Submit'}
        </Button>
      </form>
    </Container>
  );
};

export default AcceptProposal;
